import { useState } from "react";
import { ChevronDown, CheckCircle2, AlertTriangle, XCircle, MessageSquareWarning, X } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ValidationBadge } from "@/components/ui/ValidationBadge";
import { cn } from "@/lib/utils";
import type { AiValidationResult, AiValidationCheckResult, AiValidationFailure } from "@/types/ai-validation";

interface ValidationPanelProps {
  validation: AiValidationResult | null | undefined;
  className?: string;
}

function CheckStatusIcon({ status }: { status: AiValidationCheckResult["status"] }) {
  if (status === "PASS") {
    return <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />;
  }
  if (status === "WARNING") {
    return <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0 mt-0.5" />;
  }
  return <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />;
}

// FR-6: per-failure detail. Distinct-but-non-alarming -- the "info" status token, not the
// red/yellow/green vocabulary already used by CheckStatusIcon and the escalation banner.
function FailureDetail({ failure }: { failure: AiValidationFailure }) {
  return (
    <div className="rounded-md border border-status-info-mid/25 bg-status-info-soft/40 px-2.5 py-2">
      <p className="text-xs font-medium text-foreground">{failure.check}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        <span className="font-medium text-foreground">Suggested fix: </span>
        {failure.corrective_alternative}
      </p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        <span className="font-medium text-foreground">What changed: </span>
        {failure.what_changed}
      </p>
    </div>
  );
}

// FR-4: dismissal is keyed by the verdict's own content, not a server id (none exists on this
// shape) — a regenerated/reloaded verdict with different content produces a different key, so it
// re-shows even though the previous verdict's key is still "dismissed" in state.
function validationIdentity(validation: AiValidationResult): string {
  return JSON.stringify(validation);
}

export function ValidationPanel({ validation, className }: ValidationPanelProps) {
  const [open, setOpen] = useState(() => !!validation?.escalated);
  const [dismissedIdentity, setDismissedIdentity] = useState<string | null>(null);

  // FR-1: silent success — nothing renders on PASS, UNAVAILABLE, or no result at all. Only a
  // WARNING or FAIL verdict has anything to say.
  if (!validation || !validation.available) return null;
  if (validation.overall !== "WARNING" && validation.overall !== "FAIL") return null;

  const identity = validationIdentity(validation);
  if (dismissedIdentity === identity) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("w-full", className)}>
      <div
        className={cn(
          "flex items-center gap-1 w-full rounded-[8px] border transition-colors",
          validation.escalated
            ? "border-red-500/30 bg-red-500/5"
            : "border-border bg-muted/30",
        )}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-2 flex-1 min-w-0 text-left py-1.5 px-3 rounded-[8px] transition-colors",
              validation.escalated ? "hover:bg-red-500/10" : "hover:bg-muted/50",
            )}
          >
            <ValidationBadge overall={validation.overall} />
            <span className="flex-1 text-xs text-muted-foreground font-medium">
              {validation.escalated ? "Validation Failed — Please Review" : "Validation Results"}
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 shrink-0",
                open && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <button
          type="button"
          aria-label="Dismiss validation result"
          onClick={() => setDismissedIdentity(identity)}
          className="shrink-0 p-1.5 mr-1.5 rounded text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <CollapsibleContent>
        <div className="mt-1 rounded-[8px] border border-border overflow-hidden">
          {validation.escalated && (
            <div className="flex items-start gap-2.5 px-3 py-2.5 bg-red-500/5 border-b border-border">
              <MessageSquareWarning className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 dark:text-red-400 leading-relaxed">
                This output could not pass validation after multiple attempts. Please review the issues below and edit the content manually.
              </p>
            </div>
          )}
          {validation.checks.map((check, idx) => (
            <div
              key={check.check}
              className={cn(
                "flex items-start gap-2.5 px-3 py-2.5",
                (idx !== validation.checks.length - 1 ||
                  !!validation.failures?.length ||
                  validation.suggested_alternative) &&
                  "border-b border-border",
              )}
            >
              <CheckStatusIcon status={check.status} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground">{check.check}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{check.reason}</p>
              </div>
            </div>
          ))}
          {!!validation.failures?.length && (
            <div
              className={cn(
                "px-3 py-2.5 space-y-2",
                validation.suggested_alternative && "border-b border-border",
              )}
            >
              <p className="text-xs font-medium text-foreground">Corrective Guidance</p>
              <div className="space-y-2">
                {validation.failures.map((failure, idx) => (
                  <FailureDetail key={`${failure.check}-${idx}`} failure={failure} />
                ))}
              </div>
            </div>
          )}
          {validation.suggested_alternative && (
            <div className="px-3 py-2.5 bg-muted/30">
              <p className="text-xs font-medium text-foreground mb-1">Suggested Alternative</p>
              <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{validation.suggested_alternative}</p>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
